import { Dvr, Folder } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ListOfValuesReadDTO, SystemBsSrcReadDTO } from '../../api/dto';
import { getSystemChildren, getSystemRoots } from '../../api/system';
import { FilterTypeEnum } from '../../components/searching/filters';
import { SystemInfoCard } from '../../components/SystemInfoCard';
import { TreeWidget } from '../../components/tree/TreeWidget';
import { useDeviceType } from '../../hooks/useDeviceType';
import { fetchSystems } from '../../lib/api';
import { getSystemLabel } from '../../lib/label';
import { FetchUseCase } from '../fields/AsyncAutocompleteElement';
import { AsyncAutocompleteFormElement, AutocompleteFormElement, TFilterElement, TFormElement } from '../types';
import { UiSchemaType } from '../UiSchemaType';

const getNodeId = (node: SystemBsSrcReadDTO) => String(node.id);

const isLeaf = (system: SystemBsSrcReadDTO) => system.childCount === 0;

const getSystemIcon = (system: SystemBsSrcReadDTO) => {
    if (isLeaf(system)) return <Dvr />;
    return <Folder />;
};

const systemTypeProperties = (
    restrictToFacility: boolean = false,
    watchPaths?: string[] | ((path: string) => string[])
): AsyncAutocompleteFormElement['typeProperties'] => ({
    watchPaths,
    fetchOptions: (searchTerm, exactId, idField, values) =>
        fetchSystems({
            searchTerm,
            facilityCode: restrictToFacility && values ? values[0]?.code : undefined,
        }),
    fetchUseCases: [FetchUseCase.ON_KEYSTROKE],
    getOptionLabel: getSystemLabel,
    autocompleteProps: {
        disableCloseOnSelect: false,
        forcePopupIcon: false,
    },
    endAdornment: ({ field, updateValue, multiple, matchId, disabled, readOnly }) => {
        const intl = useIntl();
        const { isMobile } = useDeviceType();
        const { value } = field;
        const selectedNodes: SystemBsSrcReadDTO[] = value ? (multiple ? value : [value]) : [];
        const formContext = useFormContext();
        const paths = typeof watchPaths === 'function' ? watchPaths(field.name) : watchPaths;
        const facility: ListOfValuesReadDTO | undefined =
            formContext !== null && paths ? formContext.watch(paths)[0] : paths?.map(() => null);
        const facilityCode = facility?.code;
        return (
            <TreeWidget
                title={intl.formatMessage({ id: 'document.system.tooltip' })}
                disabled={disabled}
                readOnly={readOnly}
                TreeProps={{
                    loadRootNodes: (initialIds) => getSystemRoots({ ids: initialIds, facilityCode }),
                    loadChildrenNodes: (system: SystemBsSrcReadDTO) => getSystemChildren({ code: system.id }),
                    getNodeLabel: (system: SystemBsSrcReadDTO) => (
                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            spacing={isMobile ? 0 : 1}
                            alignItems={isMobile ? 'left' : 'center'}
                        >
                            <Typography noWrap>{system?.name}</Typography>
                        </Stack>
                    ),
                    selectedNodes,
                    onSelect: (systems: SystemBsSrcReadDTO[]) => {
                        if (!systems) return;
                        if (systems.length === 0) {
                            updateValue && updateValue(null);
                            return;
                        }
                        if (systems.length === 1 && !multiple) {
                            updateValue && updateValue(matchId ? getNodeId(systems[0]) : systems[0]);
                        } else {
                            updateValue && updateValue(matchId ? systems.map((e: any) => getNodeId(e)) : systems);
                        }
                    },
                    getNodeId,
                    getParent: (system: SystemBsSrcReadDTO) => String(system.parentId),
                    isRootNode: (system: SystemBsSrcReadDTO) => system.level === 1,
                    multiSelect: multiple,
                    isLeafNode: isLeaf,
                    keys: [
                        field.name,
                        String(facilityCode),
                        ...selectedNodes.map((node: SystemBsSrcReadDTO) => String(node.id)),
                    ],
                    isSelectable: (system) => system.visible && system.level > 1,
                    tooltipContent: (system) => <SystemInfoCard system={system} />,
                    icon: getSystemIcon,
                }}
            />
        );
    },
});

interface SystemSchemaParameters extends Pick<AutocompleteFormElement, 'path' | 'label' | 'required'> {
    restrictToFacility?: boolean;
    watchPaths?: string[] | ((path: string) => string[]);
}

export const systemSchema = ({
    path,
    label,
    required,
    restrictToFacility,
    watchPaths,
}: SystemSchemaParameters): TFormElement => ({
    type: UiSchemaType.AUTOCOMPLETE_ASYNC,
    label,
    path,
    required,
    typeProperties: systemTypeProperties(restrictToFacility, watchPaths),
});
interface SystemFilterSchemaParameters extends Pick<TFilterElement, 'id' | 'label'> {}

export const systemFilterSchema = ({ id, label }: SystemFilterSchemaParameters): TFilterElement => ({
    type: FilterTypeEnum.AUTOCOMPLETE_ASYNC,
    id,
    label,
    valueGetter: (value: SystemBsSrcReadDTO) => value.id,
    typeProperties: systemTypeProperties(),
});
